import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Download,
  Upload,
  Plus,
  Search,
  Trash2,
  Pencil,
  X,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  FileDown,
  ShieldCheck,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface VocabRow {
  id: number;
  book_slug: string;
  chapter_position: number;
  vocab_position: number;
  arabic: string;
  german: string;
  word_type: string;
  gender?: string | null;
  root?: string | null;
  singular?: string | null;
  plural?: string | null;
  feminine_singular?: string | null;
  feminine_plural?: string | null;
  verb_past?: string | null;
  verb_present?: string | null;
  verb_imperative?: string | null;
  verbal_noun?: string | null;
  notes?: string | null;
  example_arabic?: string | null;
  example_german?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

type VocabFormData = Omit<VocabRow, 'id' | 'created_at' | 'updated_at'>;
type ImportMode = 'insert_new' | 'update_existing';

interface ImportIssue {
  rowNumber: number;
  type:
    | 'error'
    | 'csv_position_duplicate'
    | 'csv_content_duplicate'
    | 'existing_position'
    | 'existing_content';
  message: string;
  key?: string;
  existingId?: number;
}

interface AnalyzedImportRow {
  rowNumber: number;
  raw: Record<string, string>;
  payload: Record<string, unknown>;
  positionKey: string;
  contentKey: string;
  existingByPosition?: VocabRow;
  existingByContent?: VocabRow;
  errors: ImportIssue[];
  warnings: ImportIssue[];
}

interface ImportAnalysis {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  newRows: number;
  updateRows: number;
  skippedRows: number;
  errors: ImportIssue[];
  warnings: ImportIssue[];
  rows: AnalyzedImportRow[];
}

interface DuplicateGroup {
  key: string;
  type: 'position' | 'content';
  label: string;
  rows: VocabRow[];
}

interface ExistingDuplicateAnalysis {
  positionDuplicates: DuplicateGroup[];
  contentDuplicates: DuplicateGroup[];
  totalDuplicateGroups: number;
  totalAffectedRows: number;
}

interface Props {
  user?: {
    id?: string;
    email?: string;
  } | null;
  cardClass: string;
  inputClass: string;
  primaryBtn: string;
  secondaryBtn: string;
  dangerBtn?: string;
  showConfirmDialog: (message: string, action: () => void) => void;
  formatDate: (dateString: string) => string;
}

const EMPTY_FORM: VocabFormData = {
  book_slug: '',
  chapter_position: 1,
  vocab_position: 1,
  arabic: '',
  german: '',
  word_type: 'noun',
  gender: '',
  root: '',
  singular: '',
  plural: '',
  feminine_singular: '',
  feminine_plural: '',
  verb_past: '',
  verb_present: '',
  verb_imperative: '',
  verbal_noun: '',
  notes: '',
  example_arabic: '',
  example_german: '',
};

const CSV_FIELDS = [
  'book_slug',
  'chapter_position',
  'vocab_position',
  'arabic',
  'german',
  'word_type',
  'gender',
  'root',
  'singular',
  'plural',
  'feminine_singular',
  'feminine_plural',
  'verb_past',
  'verb_present',
  'verb_imperative',
  'verbal_noun',
  'notes',
  'example_arabic',
  'example_german',
] as const;

const KNOWN_FIELDS = [...CSV_FIELDS];

const REQUIRED_FIELDS = [
  'book_slug',
  'chapter_position',
  'vocab_position',
  'arabic',
  'german',
] as const;

const normalizeText = (value: unknown) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const normalizeArabic = (value: unknown) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[ًٌٍَُِّْٰـ]/g, '');

const createPositionKey = (row: {
  book_slug?: string | null;
  chapter_position?: string | number | null;
  vocab_position?: string | number | null;
}) =>
  [
    normalizeText(row.book_slug),
    String(Number(row.chapter_position) || 0),
    String(Number(row.vocab_position) || 0),
  ].join('|');

const createContentKey = (row: {
  book_slug?: string | null;
  arabic?: string | null;
  german?: string | null;
}) =>
  [
    normalizeText(row.book_slug),
    normalizeArabic(row.arabic),
    normalizeText(row.german),
  ].join('|');

const VocabularyDatabaseManager: React.FC<Props> = ({
  user,
  cardClass,
  inputClass,
  primaryBtn,
  secondaryBtn,
  dangerBtn,
  showConfirmDialog,
  formatDate,
}) => {
  void user;
  void dangerBtn;
  void formatDate;

  const [vocabulary, setVocabulary] = useState<VocabRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [bookFilter, setBookFilter] = useState('all');
  const [chapterFilter, setChapterFilter] = useState('all');
  const [wordTypeFilter, setWordTypeFilter] = useState('all');

  const [editingRow, setEditingRow] = useState<VocabRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<VocabFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);

  const [importMode, setImportMode] = useState<ImportMode>('insert_new');
  const [importPreview, setImportPreview] = useState<Record<string, string>[] | null>(null);
  const [importAnalysis, setImportAnalysis] = useState<ImportAnalysis | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [existingDuplicateAnalysis, setExistingDuplicateAnalysis] =
  useState<ExistingDuplicateAnalysis | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 5000);
  };

  const loadVocabulary = useCallback(async () => {
    setLoading(true);

    try {
      const allRows: VocabRow[] = [];
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('vocabulary')
          .select('*')
          .order('book_slug')
          .order('chapter_position')
          .order('vocab_position')
          .range(from, from + PAGE_SIZE - 1);

        if (error) throw error;

        const rows = (data ?? []) as VocabRow[];
        allRows.push(...rows);
        hasMore = rows.length === PAGE_SIZE;
        from += PAGE_SIZE;
      }

      setVocabulary(allRows);

      if (allRows.length > 0) {
        setAvailableColumns(Object.keys(allRows[0]));
      } else {
        setAvailableColumns([...KNOWN_FIELDS]);
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Fehler beim Laden der Vokabeln:', error);
      showFeedback('error', 'Fehler beim Laden der Vokabel-Datenbank.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVocabulary();
  }, [loadVocabulary]);

  const books = useMemo(() => {
    const set = new Set(vocabulary.map((v) => v.book_slug).filter(Boolean));
    return Array.from(set).sort();
  }, [vocabulary]);

  const chapters = useMemo(() => {
    if (bookFilter === 'all') return [];

    const set = new Set(
      vocabulary
        .filter((v) => v.book_slug === bookFilter)
        .map((v) => v.chapter_position)
        .filter((v) => Number.isFinite(Number(v)))
    );

    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [vocabulary, bookFilter]);

  const wordTypes = useMemo(() => {
    const set = new Set(vocabulary.map((v) => v.word_type).filter(Boolean));
    return Array.from(set).sort();
  }, [vocabulary]);

  const filtered = useMemo(() => {
    return vocabulary.filter((v) => {
      const q = normalizeText(searchQuery);

      const matchesSearch =
        !q ||
        normalizeText(v.arabic).includes(q) ||
        normalizeText(v.german).includes(q) ||
        normalizeText(v.root).includes(q) ||
        normalizeText(v.notes).includes(q) ||
        normalizeText(v.book_slug).includes(q);

      const matchesBook = bookFilter === 'all' || v.book_slug === bookFilter;
      const matchesChapter =
        chapterFilter === 'all' || Number(v.chapter_position) === Number(chapterFilter);
      const matchesType = wordTypeFilter === 'all' || v.word_type === wordTypeFilter;

      return matchesSearch && matchesBook && matchesChapter && matchesType;
    });
  }, [vocabulary, searchQuery, bookFilter, chapterFilter, wordTypeFilter]);

  const stats = useMemo(() => {
    const totalBooks = new Set(vocabulary.map((v) => v.book_slug)).size;
    const totalChapters = new Set(
      vocabulary.map((v) => `${v.book_slug}::${v.chapter_position}`)
    ).size;

    const verbs = vocabulary.filter((v) => v.word_type === 'verb').length;
    const nouns = vocabulary.filter((v) => v.word_type === 'noun').length;
    const other = vocabulary.length - verbs - nouns;

    return {
      total: vocabulary.length,
      totalBooks,
      totalChapters,
      verbs,
      nouns,
      other,
    };
  }, [vocabulary]);

  const hasColumn = (column: string) =>
    availableColumns.length === 0 || availableColumns.includes(column);

  const buildPayload = (data: Partial<VocabFormData> | Record<string, unknown>) => {
    const payload: Record<string, unknown> = {};

    for (const key of KNOWN_FIELDS) {
      const rawValue = (data as Record<string, unknown>)[key];

      if (rawValue === undefined) continue;

      if (key === 'chapter_position' || key === 'vocab_position') {
        payload[key] = Number(rawValue) || 0;
      } else if (key === 'word_type') {
        payload[key] = String(rawValue || 'noun').trim() || 'noun';
      } else {
        const value = String(rawValue ?? '').trim();
        payload[key] = value === '' ? null : value;
      }
    }

    if (availableColumns.length > 0) {
      for (const key of Object.keys(payload)) {
        if (!availableColumns.includes(key)) {
          delete payload[key];
        }
      }
    }

    return payload;
  };

  const findManualDuplicates = (row: VocabFormData) => {
    const warnings: string[] = [];

    const positionDup = vocabulary.find(
      (v) =>
        createPositionKey(v) === createPositionKey(row) &&
        v.id !== editingRow?.id
    );

    if (positionDup) {
      warnings.push(
        `Position ${row.book_slug} / Kap. ${row.chapter_position} / Pos. ${row.vocab_position} existiert bereits.`
      );
    }

    const contentDup = vocabulary.find(
      (v) =>
        createContentKey(v) === createContentKey(row) &&
        v.id !== editingRow?.id
    );

    if (contentDup) {
      warnings.push(
        `"${row.arabic}" / "${row.german}" existiert bereits in ${row.book_slug}.`
      );
    }

    return warnings;
  };

  const duplicateWarnings = useMemo(() => {
    if (!editingRow && !creating) return [];
    return findManualDuplicates(formData);
  }, [formData, editingRow, creating, vocabulary]);

  const handleSave = async () => {
    if (!formData.book_slug || !formData.arabic || !formData.german) {
      showFeedback('error', 'Bitte mindestens Buch, Arabisch und Deutsch ausfüllen.');
      return;
    }

    setSaving(true);

    try {
      const payload = buildPayload(formData);

      if (editingRow) {
        const { data, error } = await supabase
          .from('vocabulary')
          .update(payload)
          .eq('id', editingRow.id)
          .select()
          .maybeSingle();

        if (error) throw error;

        setVocabulary((prev) =>
          prev.map((v) =>
            v.id === editingRow.id ? ((data ?? { ...v, ...payload }) as VocabRow) : v
          )
        );

        showFeedback('success', 'Vokabel erfolgreich aktualisiert.');
      } else {
        const { data, error } = await supabase
          .from('vocabulary')
          .insert(payload)
          .select()
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setVocabulary((prev) => [...prev, data as VocabRow]);
        }

        showFeedback('success', 'Vokabel erfolgreich erstellt.');
      }

      closeModal();
    } catch (error) {
      if (import.meta.env.DEV) console.error('Fehler beim Speichern:', error);
      showFeedback('error', 'Fehler beim Speichern der Vokabel.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row: VocabRow) => {
    showConfirmDialog(
      `Vokabel "${row.arabic}" (${row.german}) wirklich löschen?`,
      async () => {
        try {
          const { error } = await supabase
            .from('vocabulary')
            .delete()
            .eq('id', row.id);

          if (error) throw error;

          setVocabulary((prev) => prev.filter((v) => v.id !== row.id));
          showFeedback('success', 'Vokabel gelöscht.');
        } catch (error) {
          if (import.meta.env.DEV) console.error('Fehler beim Löschen:', error);
          showFeedback('error', 'Fehler beim Löschen.');
        }
      }
    );
  };

  const openEdit = (row: VocabRow) => {
    setEditingRow(row);
    setCreating(false);

    setFormData({
      book_slug: row.book_slug,
      chapter_position: row.chapter_position,
      vocab_position: row.vocab_position,
      arabic: row.arabic,
      german: row.german,
      word_type: row.word_type || 'noun',
      gender: row.gender ?? '',
      root: row.root ?? '',
      singular: row.singular ?? '',
      plural: row.plural ?? '',
      feminine_singular: row.feminine_singular ?? '',
      feminine_plural: row.feminine_plural ?? '',
      verb_past: row.verb_past ?? '',
      verb_present: row.verb_present ?? '',
      verb_imperative: row.verb_imperative ?? '',
      verbal_noun: row.verbal_noun ?? '',
      notes: row.notes ?? '',
      example_arabic: row.example_arabic ?? '',
      example_german: row.example_german ?? '',
    });
  };

  const openCreate = () => {
    setEditingRow(null);
    setCreating(true);
    setFormData({ ...EMPTY_FORM });
  };

  const closeModal = () => {
    setEditingRow(null);
    setCreating(false);
    setFormData({ ...EMPTY_FORM });
  };

  const escapeCsvCell = (value: unknown) => {
    const stringValue = value == null ? '' : String(value);

    if (
      stringValue.includes(',') ||
      stringValue.includes('"') ||
      stringValue.includes('\n') ||
      stringValue.includes('\r')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  };

  const downloadCsv = (filename: string, rows: string[][]) => {
  const csv = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;',
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};

const exportCSV = () => {
  const rows = [
    [...CSV_FIELDS],
    ...filtered.map((v) =>
      CSV_FIELDS.map((field) => String((v as Record<string, unknown>)[field] ?? ''))
    ),
  ];

  downloadCsv('arabicroots-vocabulary-export.csv', rows);
};

const exportFullBackupCSV = () => {
  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .slice(0, 19);

  const rows = [
    [...CSV_FIELDS],
    ...vocabulary.map((v) =>
      CSV_FIELDS.map((field) => String((v as Record<string, unknown>)[field] ?? ''))
    ),
  ];

  downloadCsv(`arabicroots-vocabulary-backup-${timestamp}.csv`, rows);
};

  const downloadTemplate = () => {
    const exampleRow = [
      'madina_1',
      '1',
      '1',
      'كِتَاب',
      'Buch',
      'noun',
      'masculine',
      'ك ت ب',
      'كِتَاب',
      'كُتُب',
      '',
      '',
      '',
      '',
      '',
      '',
      'Beispielnotiz',
      'هٰذَا كِتَابٌ',
      'Das ist ein Buch.',
    ];

    downloadCsv('arabicroots-vocabulary-template.csv', [
      [...CSV_FIELDS],
      exampleRow,
    ]);
  };

  const splitCsvRow = (line: string): string[] => {
    const columns: string[] = [];
    let cell = '';
    let inQuotes = false;

    for (let index = 0; index < line.length; index++) {
      const char = line[index];

      if (char === '"') {
        if (inQuotes && line[index + 1] === '"') {
          cell += '"';
          index++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        columns.push(cell);
        cell = '';
      } else {
        cell += char;
      }
    }

    columns.push(cell);
    return columns;
  };

  const splitCsvLines = (text: string): string[] => {
    const lines: string[] = [];
    let current = '';
    let inQuotes = false;

    const cleanedText = text.replace(/^\uFEFF/, '');

    for (let index = 0; index < cleanedText.length; index++) {
      const char = cleanedText[index];

      if (char === '"') {
        if (inQuotes && cleanedText[index + 1] === '"') {
          current += '"';
          index++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (current.trim()) lines.push(current);
        current = '';

        if (char === '\r' && cleanedText[index + 1] === '\n') {
          index++;
        }
      } else {
        current += char;
      }
    }

    if (current.trim()) lines.push(current);

    return lines;
  };

  const analyzeImportRows = (rows: Record<string, string>[]): ImportAnalysis => {
    const existingPositionMap = new Map<string, VocabRow>();
    const existingContentMap = new Map<string, VocabRow>();

    for (const row of vocabulary) {
      existingPositionMap.set(createPositionKey(row), row);
      existingContentMap.set(createContentKey(row), row);
    }

    const csvPositionMap = new Map<string, number[]>();
    const csvContentMap = new Map<string, number[]>();

    rows.forEach((row, index) => {
      const rowNumber = index + 2;
      const positionKey = createPositionKey(row);
      const contentKey = createContentKey(row);

      csvPositionMap.set(positionKey, [...(csvPositionMap.get(positionKey) ?? []), rowNumber]);
      csvContentMap.set(contentKey, [...(csvContentMap.get(contentKey) ?? []), rowNumber]);
    });

    const analyzedRows: AnalyzedImportRow[] = [];

    rows.forEach((row, index) => {
      const rowNumber = index + 2;
      const errors: ImportIssue[] = [];
      const warnings: ImportIssue[] = [];

      for (const field of REQUIRED_FIELDS) {
        if (!String(row[field] ?? '').trim()) {
          errors.push({
            rowNumber,
            type: 'error',
            message: `Zeile ${rowNumber}: Pflichtfeld "${field}" fehlt.`,
          });
        }
      }

      const chapterNumber = Number(row.chapter_position);
      const vocabNumber = Number(row.vocab_position);

      if (!Number.isFinite(chapterNumber) || chapterNumber <= 0) {
        errors.push({
          rowNumber,
          type: 'error',
          message: `Zeile ${rowNumber}: chapter_position muss eine positive Zahl sein.`,
        });
      }

      if (!Number.isFinite(vocabNumber) || vocabNumber <= 0) {
        errors.push({
          rowNumber,
          type: 'error',
          message: `Zeile ${rowNumber}: vocab_position muss eine positive Zahl sein.`,
        });
      }

      const normalizedRaw = {
        ...row,
        word_type: String(row.word_type || 'noun').trim() || 'noun',
      };

      const payload = buildPayload(normalizedRaw);
      const positionKey = createPositionKey(normalizedRaw);
      const contentKey = createContentKey(normalizedRaw);

      const positionRows = csvPositionMap.get(positionKey) ?? [];
      const contentRows = csvContentMap.get(contentKey) ?? [];

      if (positionRows.length > 1) {
        warnings.push({
          rowNumber,
          type: 'csv_position_duplicate',
          key: positionKey,
          message: `Zeile ${rowNumber}: Position kommt mehrfach in der CSV vor (${positionRows.join(', ')}).`,
        });
      }

      if (contentRows.length > 1) {
        warnings.push({
          rowNumber,
          type: 'csv_content_duplicate',
          key: contentKey,
          message: `Zeile ${rowNumber}: gleicher Inhalt kommt mehrfach in der CSV vor (${contentRows.join(', ')}).`,
        });
      }

      const existingByPosition = existingPositionMap.get(positionKey);
      const existingByContent = existingContentMap.get(contentKey);

      if (existingByPosition) {
        warnings.push({
          rowNumber,
          type: 'existing_position',
          key: positionKey,
          existingId: existingByPosition.id,
          message: `Zeile ${rowNumber}: Position existiert bereits in Supabase: ${existingByPosition.book_slug} / Kap. ${existingByPosition.chapter_position} / Pos. ${existingByPosition.vocab_position}.`,
        });
      }

      if (existingByContent && existingByContent.id !== existingByPosition?.id) {
        warnings.push({
          rowNumber,
          type: 'existing_content',
          key: contentKey,
          existingId: existingByContent.id,
          message: `Zeile ${rowNumber}: Inhalt existiert bereits in Supabase: "${existingByContent.arabic}" / "${existingByContent.german}".`,
        });
      }

      analyzedRows.push({
        rowNumber,
        raw: normalizedRaw,
        payload,
        positionKey,
        contentKey,
        existingByPosition,
        existingByContent,
        errors,
        warnings,
      });
    });

    const allErrors = analyzedRows.flatMap((row) => row.errors);
    const allWarnings = analyzedRows.flatMap((row) => row.warnings);
    const validRows = analyzedRows.filter((row) => row.errors.length === 0);

    const uniqueValidRows = validRows.filter((row) => {
      const csvPositionDuplicates = csvPositionMap.get(row.positionKey) ?? [];
      return csvPositionDuplicates[0] === row.rowNumber;
    });

    const newRows = uniqueValidRows.filter((row) => !row.existingByPosition).length;
    const updateRows =
      importMode === 'update_existing'
        ? uniqueValidRows.filter((row) => row.existingByPosition).length
        : 0;

    const skippedRows =
      importMode === 'insert_new'
        ? validRows.filter((row) => row.existingByPosition).length +
          validRows.filter((row) => (csvPositionMap.get(row.positionKey) ?? [])[0] !== row.rowNumber).length
        : validRows.filter((row) => (csvPositionMap.get(row.positionKey) ?? [])[0] !== row.rowNumber).length;

    return {
      totalRows: rows.length,
      validRows: validRows.length,
      invalidRows: allErrors.length,
      newRows,
      updateRows,
      skippedRows,
      errors: allErrors,
      warnings: allWarnings,
      rows: analyzedRows,
    };
  };

  useEffect(() => {
    if (importPreview) {
      setImportAnalysis(analyzeImportRows(importPreview));
    }
  }, [importMode, importPreview, vocabulary]);

  const parseCSV = (text: string) => {
    const lines = splitCsvLines(text);

    if (lines.length < 2) {
      setImportErrors(['CSV muss mindestens eine Kopfzeile und eine Datenzeile haben.']);
      setImportPreview(null);
      setImportAnalysis(null);
      return;
    }

    const headers = splitCsvRow(lines[0]).map((header) =>
      header.trim().toLowerCase().replace(/^\uFEFF/, '')
    );

    const missing = REQUIRED_FIELDS.filter((field) => !headers.includes(field));

    if (missing.length > 0) {
      setImportErrors([`Fehlende Pflichtfelder: ${missing.join(', ')}`]);
      setImportPreview(null);
      setImportAnalysis(null);
      return;
    }

    const unknownHeaders = headers.filter((header) => !KNOWN_FIELDS.includes(header as typeof KNOWN_FIELDS[number]));

    const rows: Record<string, string>[] = [];

    for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
      const columns = splitCsvRow(lines[lineIndex]);
      const row: Record<string, string> = {};

      headers.forEach((header, columnIndex) => {
        row[header] = columns[columnIndex]?.trim() ?? '';
      });

      const hasAnyValue = Object.values(row).some((value) => value.trim() !== '');
      if (hasAnyValue) rows.push(row);
    }

    if (rows.length === 0) {
      setImportErrors(['CSV enthält keine gültigen Datenzeilen.']);
      setImportPreview(null);
      setImportAnalysis(null);
      return;
    }

    setImportErrors(
      unknownHeaders.length > 0
        ? [`Hinweis: Diese Spalten werden ignoriert, weil sie nicht zur vocabulary-Tabelle gehören: ${unknownHeaders.join(', ')}`]
        : []
    );

    setImportPreview(rows);
    setImportAnalysis(analyzeImportRows(rows));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (readerEvent) => {
      const text = String(readerEvent.target?.result ?? '');
      parseCSV(text);
    };

    reader.readAsText(file, 'UTF-8');
    event.target.value = '';
  };

  const executeImport = async () => {
    if (!importPreview || !importAnalysis) return;

    if (importAnalysis.errors.length > 0) {
      showFeedback('error', 'Import nicht möglich: Bitte behebe zuerst die Fehler in der CSV.');
      return;
    }

    const rowsToProcess = importAnalysis.rows.filter((row) => {
      if (row.errors.length > 0) return false;

      const firstRowForPosition = importAnalysis.rows.find(
        (candidate) => candidate.positionKey === row.positionKey
      );

      if (firstRowForPosition?.rowNumber !== row.rowNumber) return false;

      if (importMode === 'insert_new' && row.existingByPosition) return false;

      return true;
    });

    if (rowsToProcess.length === 0) {
      showFeedback('error', 'Es gibt keine importierbaren Zeilen für den gewählten Modus.');
      return;
    }

    setImporting(true);

    try {
      const rowsToInsert = rowsToProcess.filter((row) => !row.existingByPosition);
      const rowsToUpdate =
        importMode === 'update_existing'
          ? rowsToProcess.filter((row) => row.existingByPosition)
          : [];

      if (rowsToInsert.length > 0) {
        const { error } = await supabase
          .from('vocabulary')
          .insert(rowsToInsert.map((row) => row.payload));

        if (error) throw error;
      }

      for (const row of rowsToUpdate) {
        if (!row.existingByPosition) continue;

        const { error } = await supabase
          .from('vocabulary')
          .update(row.payload)
          .eq('id', row.existingByPosition.id);

        if (error) throw error;
      }

      showFeedback(
        'success',
        `Import abgeschlossen: ${rowsToInsert.length} neu eingefügt, ${rowsToUpdate.length} aktualisiert.`
      );

      setImportPreview(null);
      setImportAnalysis(null);
      await loadVocabulary();
    } catch (error) {
      if (import.meta.env.DEV) console.error('Import-Fehler:', error);
      showFeedback('error', 'Fehler beim Importieren der Vokabeln.');
    } finally {
      setImporting(false);
    }
  };

  const clearImport = () => {
    setImportPreview(null);
    setImportAnalysis(null);
    setImportErrors([]);
  };

  const analyzeExistingDuplicates = () => {
  const positionMap = new Map<string, VocabRow[]>();
  const contentMap = new Map<string, VocabRow[]>();

  for (const row of vocabulary) {
    const positionKey = createPositionKey(row);
    const contentKey = createContentKey(row);

    positionMap.set(positionKey, [...(positionMap.get(positionKey) ?? []), row]);
    contentMap.set(contentKey, [...(contentMap.get(contentKey) ?? []), row]);
  }

  const positionDuplicates: DuplicateGroup[] = Array.from(positionMap.entries())
    .filter(([, rows]) => rows.length > 1)
    .map(([key, rows]) => {
      const first = rows[0];

      return {
        key,
        type: 'position',
        label: `${first.book_slug} / Kapitel ${first.chapter_position} / Position ${first.vocab_position}`,
        rows: rows.sort((a, b) => Number(a.id) - Number(b.id)),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));

  const contentDuplicates: DuplicateGroup[] = Array.from(contentMap.entries())
    .filter(([, rows]) => rows.length > 1)
    .map(([key, rows]) => {
      const first = rows[0];

      return {
        key,
        type: 'content',
        label: `${first.book_slug} / ${first.arabic} / ${first.german}`,
        rows: rows.sort((a, b) => Number(a.id) - Number(b.id)),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));

  const affectedIds = new Set<number>();

  for (const group of [...positionDuplicates, ...contentDuplicates]) {
    for (const row of group.rows) {
      affectedIds.add(row.id);
    }
  }

  const analysis: ExistingDuplicateAnalysis = {
    positionDuplicates,
    contentDuplicates,
    totalDuplicateGroups: positionDuplicates.length + contentDuplicates.length,
    totalAffectedRows: affectedIds.size,
  };

  setExistingDuplicateAnalysis(analysis);

  if (analysis.totalDuplicateGroups === 0) {
    showFeedback('success', 'Keine Dubletten im aktuellen Datensatz gefunden.');
  } else {
    showFeedback(
      'error',
      `${analysis.totalDuplicateGroups} Dubletten-Gruppen gefunden. ${analysis.totalAffectedRows} Datensätze sind betroffen.`
    );
  }
};

const clearExistingDuplicateAnalysis = () => {
  setExistingDuplicateAnalysis(null);
};

  const isModalOpen = editingRow !== null || creating;

  const issuePreview = useMemo(() => {
    if (!importAnalysis) return [];

    return [...importAnalysis.errors, ...importAnalysis.warnings].slice(0, 15);
  }, [importAnalysis]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#970000]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-2">
            Vokabel-Datenbank
          </p>
          <h2 className="text-3xl font-bold text-[#1f1f1f]">Alle Vokabeln verwalten</h2>
          <p className="text-sm text-[#6b6b6b] mt-2">
            Suche, bearbeite, exportiere und importiere deine App-Vokabeln zentral im Dashboard.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={loadVocabulary} className={secondaryBtn}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </button>

          <button type="button" onClick={openCreate} className={primaryBtn}>
            <Plus className="h-4 w-4 mr-2" />
            Neue Vokabel
          </button>
        </div>
      </div>

      {feedback && (
        <div
          className={`flex items-center gap-3 px-5 py-3 rounded-2xl text-sm font-medium ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          {feedback.message}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Gesamt', value: stats.total },
          { label: 'Bücher', value: stats.totalBooks },
          { label: 'Kapitel', value: stats.totalChapters },
          { label: 'Nomen', value: stats.nouns },
          { label: 'Verben', value: stats.verbs },
          { label: 'Sonstige', value: stats.other },
        ].map((stat) => (
          <div key={stat.label} className={`${cardClass} p-4 text-center`}>
            <p className="text-2xl font-bold text-[#1f1f1f]">{stat.value}</p>
            <p className="text-xs text-[#6b6b6b] mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className={`${cardClass} p-4`}>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b6b6b]" />
            <input
              placeholder="Suche nach Arabisch, Deutsch, Wurzel, Notiz..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className={`${inputClass} pl-10`}
            />
          </div>

          <select
            value={bookFilter}
            onChange={(event) => {
              setBookFilter(event.target.value);
              setChapterFilter('all');
            }}
            className={inputClass}
          >
            <option value="all">Alle Bücher</option>
            {books.map((book) => (
              <option key={book} value={book}>
                {book}
              </option>
            ))}
          </select>

          <select
            value={chapterFilter}
            onChange={(event) => setChapterFilter(event.target.value)}
            className={inputClass}
            disabled={bookFilter === 'all'}
          >
            <option value="all">Alle Kapitel</option>
            {chapters.map((chapter) => (
              <option key={chapter} value={String(chapter)}>
                Kapitel {chapter}
              </option>
            ))}
          </select>

          <select
            value={wordTypeFilter}
            onChange={(event) => setWordTypeFilter(event.target.value)}
            className={inputClass}
          >
            <option value="all">Alle Wortarten</option>
            {wordTypes.map((wordType) => (
              <option key={wordType} value={wordType}>
                {wordType}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`${cardClass} p-4`}>
  <div className="flex flex-wrap items-center gap-3">
    <button type="button" onClick={exportCSV} className={secondaryBtn}>
      <Download className="h-4 w-4 mr-2" />
      Gefilterte CSV exportieren ({filtered.length})
    </button>

    <button type="button" onClick={exportFullBackupCSV} className={secondaryBtn}>
      <ShieldCheck className="h-4 w-4 mr-2" />
      Komplettes Backup exportieren ({vocabulary.length})
    </button>

    <button type="button" onClick={analyzeExistingDuplicates} className={secondaryBtn}>
      <AlertTriangle className="h-4 w-4 mr-2" />
      Dubletten prüfen
    </button>

    <button type="button" onClick={downloadTemplate} className={secondaryBtn}>
      <FileDown className="h-4 w-4 mr-2" />
      CSV-Vorlage
    </button>

    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      className={secondaryBtn}
    >
      <Upload className="h-4 w-4 mr-2" />
      CSV importieren
    </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileSelect}
          />

          <span className="text-xs text-[#6b6b6b]">
            {filtered.length} von {vocabulary.length} Vokabeln angezeigt
          </span>
        </div>
      </div>

      {existingDuplicateAnalysis && (
  <div className={`${cardClass} p-6 space-y-5`}>
    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
      <div>
        <h3 className="text-xl font-bold text-[#1f1f1f]">
          Dublettenprüfung im bestehenden Datensatz
        </h3>
        <p className="text-sm text-[#6b6b6b] mt-1">
          Diese Prüfung analysiert alle aktuell geladenen Vokabeln aus der Tabelle vocabulary.
        </p>
      </div>

      <button type="button" onClick={clearExistingDuplicateAnalysis} className={secondaryBtn}>
        Schließen
      </button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="rounded-2xl border border-[#eadfd6] bg-[#fcfaf8] p-3 text-center">
        <p className="text-xl font-bold text-[#1f1f1f]">
          {existingDuplicateAnalysis.totalDuplicateGroups}
        </p>
        <p className="text-xs text-[#6b6b6b] mt-1">Dubletten-Gruppen</p>
      </div>

      <div className="rounded-2xl border border-[#eadfd6] bg-[#fcfaf8] p-3 text-center">
        <p className="text-xl font-bold text-[#1f1f1f]">
          {existingDuplicateAnalysis.totalAffectedRows}
        </p>
        <p className="text-xs text-[#6b6b6b] mt-1">Betroffene Datensätze</p>
      </div>

      <div className="rounded-2xl border border-[#eadfd6] bg-[#fcfaf8] p-3 text-center">
        <p className="text-xl font-bold text-[#1f1f1f]">
          {existingDuplicateAnalysis.positionDuplicates.length}
        </p>
        <p className="text-xs text-[#6b6b6b] mt-1">Positions-Dubletten</p>
      </div>

      <div className="rounded-2xl border border-[#eadfd6] bg-[#fcfaf8] p-3 text-center">
        <p className="text-xl font-bold text-[#1f1f1f]">
          {existingDuplicateAnalysis.contentDuplicates.length}
        </p>
        <p className="text-xs text-[#6b6b6b] mt-1">Inhalts-Dubletten</p>
      </div>
    </div>

    {existingDuplicateAnalysis.totalDuplicateGroups === 0 ? (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-green-800 text-sm font-medium">
        Keine Dubletten gefunden.
      </div>
    ) : (
      <div className="space-y-6">
        {existingDuplicateAnalysis.positionDuplicates.length > 0 && (
          <div>
            <h4 className="font-semibold text-[#1f1f1f] mb-3">
              Positions-Dubletten
            </h4>

            <div className="space-y-3 max-h-96 overflow-auto pr-1">
              {existingDuplicateAnalysis.positionDuplicates.map((group) => (
                <div
                  key={`position-${group.key}`}
                  className="rounded-2xl border border-red-200 bg-red-50 p-4"
                >
                  <p className="font-semibold text-red-900 mb-2">
                    {group.label}
                  </p>

                  <div className="overflow-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-red-900">
                          <th className="text-left py-1 pr-3">ID</th>
                          <th className="text-left py-1 pr-3">Arabisch</th>
                          <th className="text-left py-1 pr-3">Deutsch</th>
                          <th className="text-left py-1 pr-3">Wortart</th>
                          <th className="text-left py-1 pr-3">Notiz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((row) => (
                          <tr key={row.id} className="border-t border-red-200">
                            <td className="py-1 pr-3">{row.id}</td>
                            <td className="py-1 pr-3 text-right" dir="rtl">
                              {row.arabic}
                            </td>
                            <td className="py-1 pr-3">{row.german}</td>
                            <td className="py-1 pr-3">{row.word_type}</td>
                            <td className="py-1 pr-3">{row.notes ?? ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {existingDuplicateAnalysis.contentDuplicates.length > 0 && (
          <div>
            <h4 className="font-semibold text-[#1f1f1f] mb-3">
              Inhalts-Dubletten
            </h4>

            <div className="space-y-3 max-h-96 overflow-auto pr-1">
              {existingDuplicateAnalysis.contentDuplicates.map((group) => (
                <div
                  key={`content-${group.key}`}
                  className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4"
                >
                  <p className="font-semibold text-yellow-900 mb-2">
                    {group.label}
                  </p>

                  <div className="overflow-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-yellow-900">
                          <th className="text-left py-1 pr-3">ID</th>
                          <th className="text-left py-1 pr-3">Buch</th>
                          <th className="text-left py-1 pr-3">Kap.</th>
                          <th className="text-left py-1 pr-3">Pos.</th>
                          <th className="text-left py-1 pr-3">Arabisch</th>
                          <th className="text-left py-1 pr-3">Deutsch</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((row) => (
                          <tr key={row.id} className="border-t border-yellow-200">
                            <td className="py-1 pr-3">{row.id}</td>
                            <td className="py-1 pr-3">{row.book_slug}</td>
                            <td className="py-1 pr-3">{row.chapter_position}</td>
                            <td className="py-1 pr-3">{row.vocab_position}</td>
                            <td className="py-1 pr-3 text-right" dir="rtl">
                              {row.arabic}
                            </td>
                            <td className="py-1 pr-3">{row.german}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
)}
      {importErrors.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-yellow-900 mb-2">Hinweise zum Import:</p>
          {importErrors.map((error, index) => (
            <p key={index} className="text-sm text-yellow-800">
              {error}
            </p>
          ))}
          <button
            type="button"
            onClick={() => setImportErrors([])}
            className="mt-3 text-xs text-yellow-800 underline"
          >
            Schließen
          </button>
        </div>
      )}

      {importPreview && importAnalysis && (
        <div className={`${cardClass} p-6 space-y-5`}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-[#1f1f1f]">Import-Analyse</h3>
              <p className="text-sm text-[#6b6b6b] mt-1">
                Prüfe die Analyse, wähle den Import-Modus und starte erst dann den Import.
              </p>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={clearImport} className={secondaryBtn}>
                Abbrechen
              </button>

              <button
                type="button"
                onClick={executeImport}
                disabled={importing || importAnalysis.errors.length > 0}
                className={`${primaryBtn} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {importing ? 'Importiere...' : 'Import starten'}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block rounded-2xl border border-[#eadfd6] p-4 cursor-pointer hover:bg-[#fcfaf8]">
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="importMode"
                  value="insert_new"
                  checked={importMode === 'insert_new'}
                  onChange={() => setImportMode('insert_new')}
                  className="mt-1"
                />
                <div>
                  <p className="font-semibold text-[#1f1f1f]">Nur neue hinzufügen</p>
                  <p className="text-sm text-[#6b6b6b] mt-1">
                    Bestehende Positionen werden übersprungen. Es entstehen keine neuen Dubletten nach Position.
                  </p>
                </div>
              </div>
            </label>

            <label className="block rounded-2xl border border-[#eadfd6] p-4 cursor-pointer hover:bg-[#fcfaf8]">
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="importMode"
                  value="update_existing"
                  checked={importMode === 'update_existing'}
                  onChange={() => setImportMode('update_existing')}
                  className="mt-1"
                />
                <div>
                  <p className="font-semibold text-[#1f1f1f]">
                    Vorhandene aktualisieren + neue hinzufügen
                  </p>
                  <p className="text-sm text-[#6b6b6b] mt-1">
                    Gleiche Positionen werden aktualisiert. Neue Positionen werden eingefügt.
                  </p>
                </div>
              </div>
            </label>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: 'CSV-Zeilen', value: importAnalysis.totalRows },
              { label: 'Gültig', value: importAnalysis.validRows },
              { label: 'Fehler', value: importAnalysis.errors.length },
              { label: 'Neu', value: importAnalysis.newRows },
              { label: 'Updates', value: importAnalysis.updateRows },
              { label: 'Übersprungen', value: importAnalysis.skippedRows },
              { label: 'Warnungen', value: importAnalysis.warnings.length },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-[#eadfd6] bg-[#fcfaf8] p-3 text-center">
                <p className="text-xl font-bold text-[#1f1f1f]">{item.value}</p>
                <p className="text-xs text-[#6b6b6b] mt-1">{item.label}</p>
              </div>
            ))}
          </div>

          {issuePreview.length > 0 && (
            <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm font-semibold text-yellow-900 mb-2">
                Fehler & Warnungen, erste {issuePreview.length}
              </p>

              <div className="space-y-1 max-h-56 overflow-auto">
                {issuePreview.map((issue, index) => (
                  <p
                    key={`${issue.rowNumber}-${issue.type}-${index}`}
                    className={`text-xs ${
                      issue.type === 'error' ? 'text-red-700' : 'text-yellow-800'
                    }`}
                  >
                    {issue.message}
                  </p>
                ))}
              </div>

              {[...importAnalysis.errors, ...importAnalysis.warnings].length > issuePreview.length && (
                <p className="text-xs text-yellow-800 mt-2">
                  Weitere Hinweise vorhanden. Es werden nur die ersten {issuePreview.length} angezeigt.
                </p>
              )}
            </div>
          )}

          <div className="overflow-auto max-h-96 border border-[#eadfd6] rounded-2xl">
            <table className="w-full text-sm">
              <thead className="bg-[#f8f3ef] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Status</th>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Zeile</th>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Buch</th>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Kap.</th>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Pos.</th>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Arabisch</th>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Deutsch</th>
                  <th className="text-left px-3 py-2 font-medium text-[#3f3a37]">Wortart</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#eadfd6]">
                {importAnalysis.rows.slice(0, 100).map((row) => {
                  const hasError = row.errors.length > 0;
                  const willUpdate = importMode === 'update_existing' && !!row.existingByPosition;
                  const willSkip = importMode === 'insert_new' && !!row.existingByPosition;

                  return (
                    <tr key={row.rowNumber} className="hover:bg-[#fcfaf8]">
                      <td className="px-3 py-2">
                        {hasError ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 border border-red-200">
                            Fehler
                          </span>
                        ) : willSkip ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 border border-gray-200">
                            Überspringen
                          </span>
                        ) : willUpdate ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                            Update
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 border border-green-200">
                            Neu
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#6b6b6b]">{row.rowNumber}</td>
                      <td className="px-3 py-2">{row.raw.book_slug}</td>
                      <td className="px-3 py-2">{row.raw.chapter_position}</td>
                      <td className="px-3 py-2">{row.raw.vocab_position}</td>
                      <td className="px-3 py-2 font-arabic" dir="rtl">
                        {row.raw.arabic}
                      </td>
                      <td className="px-3 py-2">{row.raw.german}</td>
                      <td className="px-3 py-2">{row.raw.word_type}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {importAnalysis.rows.length > 100 && (
              <p className="text-center text-xs text-[#6b6b6b] py-2">
                ... und {importAnalysis.rows.length - 100} weitere Zeilen
              </p>
            )}
          </div>
        </div>
      )}

      <div className={`${cardClass} overflow-hidden`}>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f8f3ef] border-b border-[#eadfd6]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Buch</th>
                <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Kap.</th>
                <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Pos.</th>
                <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Arabisch</th>
                <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Deutsch</th>
                <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Wortart</th>
                {hasColumn('root') && (
                  <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Wurzel</th>
                )}
                <th className="text-left px-4 py-3 font-semibold text-[#3f3a37]">Notizen</th>
                <th className="text-right px-4 py-3 font-semibold text-[#3f3a37]">Aktionen</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-[#eadfd6]">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-[#6b6b6b]">
                    {vocabulary.length === 0
                      ? 'Noch keine Vokabeln vorhanden.'
                      : 'Keine Vokabeln gefunden.'}
                  </td>
                </tr>
              ) : (
                filtered.slice(0, 200).map((row) => (
                  <tr key={row.id} className="hover:bg-[#fcfaf8] transition-colors">
                    <td className="px-4 py-3 text-xs font-medium text-[#5f5148]">
                      {row.book_slug}
                    </td>
                    <td className="px-4 py-3">{row.chapter_position}</td>
                    <td className="px-4 py-3">{row.vocab_position}</td>
                    <td className="px-4 py-3 font-arabic text-base" dir="rtl">
                      {row.arabic}
                    </td>
                    <td className="px-4 py-3">{row.german}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-[#f8f3ef] text-[#5f5148] border border-[#eadfd6]">
                        {row.word_type}
                      </span>
                    </td>
                    {hasColumn('root') && (
                      <td className="px-4 py-3 font-arabic text-sm" dir="rtl">
                        {row.root ?? ''}
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs text-[#6b6b6b] max-w-[150px] truncate">
                      {row.notes ?? ''}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="p-2 rounded-xl hover:bg-[#f8f3ef] text-[#5f5148] transition-colors"
                          title="Bearbeiten"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="p-2 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
                          title="Löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {filtered.length > 200 && (
            <div className="px-4 py-3 bg-[#fcfaf8] border-t border-[#eadfd6] text-center text-sm text-[#6b6b6b]">
              Es werden nur die ersten 200 Treffer angezeigt. Nutze Suche oder Filter, um die Liste einzugrenzen.
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#eadfd6]">
              <div>
                <h3 className="text-xl font-bold text-[#1f1f1f]">
                  {editingRow ? 'Vokabel bearbeiten' : 'Neue Vokabel erstellen'}
                </h3>
                <p className="text-sm text-[#6b6b6b]">
                  Änderungen werden direkt in der Supabase-Tabelle vocabulary gespeichert.
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-xl hover:bg-[#f8f3ef]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-145px)] space-y-5">
              {duplicateWarnings.length > 0 && (
                <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4">
                  <p className="font-semibold text-yellow-900 mb-2">Dubletten-Warnung</p>
                  {duplicateWarnings.map((warning, index) => (
                    <p key={index} className="text-sm text-yellow-800">
                      {warning}
                    </p>
                  ))}
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Buch
                  </label>
                  <input
                    value={formData.book_slug}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, book_slug: event.target.value }))
                    }
                    className={inputClass}
                    placeholder="madina_1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Kapitel
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.chapter_position}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        chapter_position: Number(event.target.value) || 1,
                      }))
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Position
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.vocab_position}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        vocab_position: Number(event.target.value) || 1,
                      }))
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Arabisch
                  </label>
                  <input
                    value={formData.arabic}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, arabic: event.target.value }))
                    }
                    className={`${inputClass} text-right`}
                    dir="rtl"
                    placeholder="كِتَاب"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Deutsch
                  </label>
                  <input
                    value={formData.german}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, german: event.target.value }))
                    }
                    className={inputClass}
                    placeholder="Buch"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Wortart
                  </label>
                  <input
                    value={formData.word_type}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, word_type: event.target.value }))
                    }
                    className={inputClass}
                    placeholder="noun / verb / particle"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Geschlecht
                  </label>
                  <input
                    value={formData.gender ?? ''}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, gender: event.target.value }))
                    }
                    className={inputClass}
                    placeholder="masculine / feminine"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Wurzel
                  </label>
                  <input
                    value={formData.root ?? ''}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, root: event.target.value }))
                    }
                    className={`${inputClass} text-right`}
                    dir="rtl"
                    placeholder="ك ت ب"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                {[
                  ['singular', 'Singular'],
                  ['plural', 'Plural'],
                  ['feminine_singular', 'Feminin Singular'],
                  ['feminine_plural', 'Feminin Plural'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                      {label}
                    </label>
                    <input
                      value={String((formData as Record<string, unknown>)[key] ?? '')}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, [key]: event.target.value }))
                      }
                      className={`${inputClass} text-right`}
                      dir="rtl"
                    />
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                {[
                  ['verb_past', 'Vergangenheit'],
                  ['verb_present', 'Gegenwart'],
                  ['verb_imperative', 'Imperativ'],
                  ['verbal_noun', 'Masdar'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                      {label}
                    </label>
                    <input
                      value={String((formData as Record<string, unknown>)[key] ?? '')}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, [key]: event.target.value }))
                      }
                      className={`${inputClass} text-right`}
                      dir="rtl"
                    />
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Beispielsatz Arabisch
                  </label>
                  <textarea
                    value={formData.example_arabic ?? ''}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, example_arabic: event.target.value }))
                    }
                    className={`${inputClass} min-h-[90px] text-right`}
                    dir="rtl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                    Beispielsatz Deutsch
                  </label>
                  <textarea
                    value={formData.example_german ?? ''}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, example_german: event.target.value }))
                    }
                    className={`${inputClass} min-h-[90px]`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3f3a37] mb-1">
                  Notizen
                </label>
                <textarea
                  value={formData.notes ?? ''}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  className={`${inputClass} min-h-[90px]`}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#eadfd6] bg-[#fcfaf8]">
              <button type="button" onClick={closeModal} className={secondaryBtn}>
                Abbrechen
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={`${primaryBtn} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {saving ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyDatabaseManager;